import React from 'react';

import { useIntl } from 'react-intl-next';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { type PreviewButton, PreviewScreen } from '@atlassian/generative-ai-modal/screens/Preview';

import type { AIPlugin } from '../../editor-plugin-ai';
import { insertRecommendation } from '../../pm-plugins/ai-proactive/commands';
import type { AIProactivePluginState } from '../../pm-plugins/ai-proactive/states';
import { convertPMFragmentToPreview } from '../../ui/convert-markdown-to-preview';
import { messages } from '../messages';

import { copyResponseToClipboard } from './utils/copy-response';

type ProactivePreviewScreenWithLogicProps = {
	aiProactivePluginState: AIProactivePluginState;
	editorView: EditorView;
	providerFactory: ProviderFactory;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
};

export function ProactivePreviewScreenWithLogic({
	aiProactivePluginState,
	editorView,
	providerFactory,
	api,
}: ProactivePreviewScreenWithLogicProps) {
	const { formatMessage } = useIntl();
	const { selectedRecommendationId, proactiveAIBlocks } = aiProactivePluginState;

	const currentlySelected = proactiveAIBlocks.find(
		(obj) =>
			obj.recommendations &&
			obj.recommendations?.length > 0 &&
			obj.recommendations[0].id === selectedRecommendationId,
	);

	if (!currentlySelected?.recommendations?.length) {
		return null;
	}
	const pmFragment = currentlySelected.recommendations[0].transformContent.content;

	const preview = convertPMFragmentToPreview({
		schema: editorView.state.schema,
		pmFragment: pmFragment,
		showTelepointer: false,
		providerFactory: providerFactory,
	});

	const proactiveRecommendationButtons: PreviewButton[] = [
		{
			label: formatMessage(messages.dismissProactiveRecommentation),
			appearance: 'secondary',
			onClick: () => {
				// removeRecommendation
			},
		},
		{
			label: formatMessage(messages.insertProactiveRecommentation),
			appearance: 'primary',
			onClick: () => {
				const applyChange = api?.contextPanel?.actions.applyChange;
				if (!currentlySelected.recommendations) {
					return null;
				}
				insertRecommendation(
					currentlySelected,
					currentlySelected.recommendations[0],
					applyChange,
				)(editorView.state, editorView.dispatch);
			},
		},
	];

	return (
		<PreviewScreen
			staticPromptTitle={formatMessage(messages.temporaryRecommendationPromptTitle)}
			staticInsertedPrompt={formatMessage(messages.temporaryRecommendationPrompt)}
			preview={preview}
			disableActions={false}
			disableInterrogation={true}
			schema={editorView.state.schema}
			providerFactory={providerFactory}
			onCopy={() => {
				// add in analytics
				copyResponseToClipboard({ editorView, pmFragment });
			}}
			showBack={false}
			onGoodResponseButtonClick={() => {}}
			onBadResponseButtonClick={() => {}}
			applications={proactiveRecommendationButtons}
			isInputActive={false}
			actionButtonsLayoutMode={'compact'}
		/>
	);
}
